/**
 * The "new since you last looked" dot on the profile menu.
 *
 * The version the person last opened the What's new panel at is kept in
 * localStorage. Storage can be blocked (private windows, cleared site data), so
 * every access is wrapped; when it fails the dot simply does not show, rather
 * than nagging on every load.
 */

import { useCallback, useEffect, useState } from "react";
import { APP_VERSION } from "./error-report.js";

export const SEEN_KEY = "astrum.whatsNew.seenVersion";
const CHANGE_EVENT = "astrum:whats-new-seen";

export function readSeenVersion(): string | null {
  try {
    return localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function writeSeenVersion(version: string): boolean {
  try {
    localStorage.setItem(SEEN_KEY, version);
    return true;
  } catch {
    return false;
  }
}

/** A dot is due when this build is a different one from the last the person opened. */
export function hasUnseenVersion(current: string, seen: string | null, storageWorks = true): boolean {
  if (!storageWorks || current === "dev") return false;
  return seen !== current;
}

function storageWorks(): boolean {
  try {
    localStorage.getItem(SEEN_KEY);
    return true;
  } catch {
    return false;
  }
}

export function useWhatsNewDot(): { unseen: boolean; markSeen: () => void } {
  const [seen, setSeen] = useState<string | null>(readSeenVersion);

  useEffect(() => {
    const sync = () => setSeen(readSeenVersion());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const markSeen = useCallback(() => {
    if (writeSeenVersion(APP_VERSION)) window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return { unseen: hasUnseenVersion(APP_VERSION, seen, storageWorks()), markSeen };
}
